import { useRef, useState, type FormEvent } from "react";
import { useSeo } from "../lib/seo";
import { SUPPORT_TOPICS, canSubmitSupportForm, publicError, supportRequestSchema } from "../lib/security";

type FormStatus = "idle" | "sending" | "sent" | "error";

export default function SupportPage() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [message, setMessage] = useState("");
  const openedAt = useRef(Date.now());
  useSeo({ title: "Support | Shortcuts Library", description: "Contact Shortcuts Library support about shortcuts, purchases, privacy, sync, ads, or technical problems.", path: "/support", image: "/og/support.png" });

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    if (String(form.get("company") ?? "") || Date.now() - openedAt.current < 800) {
      setStatus("sent");
      setMessage("Your request was sent. Support will review the details you provided.");
      formElement.reset();
      return;
    }
    const parsed = supportRequestSchema.safeParse({
      name: form.get("name"),
      email: form.get("email"),
      topic: form.get("topic"),
      message: form.get("message"),
      app_version: String(form.get("appVersion") ?? "").trim() || undefined,
    });
    if (!parsed.success) {
      setStatus("error");
      setMessage(parsed.error.issues[0]?.message ?? "Check the form and try again.");
      return;
    }
    if (!canSubmitSupportForm()) {
      setStatus("error");
      setMessage("Too many requests. Wait a minute and try again.");
      return;
    }
    setStatus("sending");
    try {
      const { supabase } = await import("../lib/supabase");
      if (!supabase) {
        setStatus("error");
        setMessage("Support submission is unavailable. Please try again later.");
        return;
      }
      const { error } = await supabase.rpc("submit_website_support", {
        p_name: parsed.data.name,
        p_email: parsed.data.email,
        p_topic: parsed.data.topic,
        p_message: parsed.data.message,
        p_app_version: parsed.data.app_version ?? null,
      });
      if (error) {
        setStatus("error");
        setMessage(publicError(error, "Your request was not sent. Try again later."));
        return;
      }
      setStatus("sent");
      setMessage("Your request was sent. Support will review the details you provided.");
      formElement.reset();
      openedAt.current = Date.now();
    } catch (error) {
      setStatus("error");
      setMessage(publicError(error, "Your request was not sent. Check your connection and try again."));
    }
  }

  return <section className="section support-page"><div className="page-heading"><span className="eyebrow">Help and contact</span><h1>How can we help?</h1><p>Include the shortcut name, app version, and the last action you took. Clear details shorten the investigation.</p></div><div className="support-layout"><form className="support-form" method="post" onSubmit={submit}><label>Name<input name="name" autoComplete="name" maxLength={100} required placeholder="Your name" /></label><label>Email<input name="email" type="email" autoComplete="email" inputMode="email" autoCapitalize="none" autoCorrect="off" maxLength={254} required placeholder="name@example.com" /></label><label>Topic<select name="topic" defaultValue="Technical problem">{SUPPORT_TOPICS.map((topic) => <option key={topic}>{topic}</option>)}</select></label><label>App version, optional<input name="appVersion" maxLength={30} placeholder="For example, 3.0.0" /></label><label>What happened<textarea name="message" rows={7} minLength={20} maxLength={5000} required placeholder="Describe what you expected, what happened, and any message you saw." /></label><label className="honeypot" aria-hidden="true">Company<input name="company" tabIndex={-1} autoComplete="off" /></label><button className="button" type="submit" disabled={status === "sending"}>{status === "sending" ? "Sending" : "Send support request"}</button>{message && <p className={`form-message ${status}`} role="status">{message}</p>}</form><aside className="support-aside"><h2>Before you send</h2><ul className="check-list"><li>Update to the newest app version.</li><li>Confirm the shortcut still exists at its source link.</li><li>For purchase access, use Restore Purchases in Settings.</li><li>For sync, confirm iCloud Drive is enabled for the app.</li></ul><h2>Useful details</h2><p>Device model, operating-system version, app version, shortcut name, and repeatable steps.</p></aside></div></section>;
}
